import React, { useState } from "react";
import { Search, History, X } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { demoCopy } from "../i18n/demoCopy";
import { insideDemo } from "../demo/demoMode";

const MAX_HISTORY = 6;
const readHistory = (key) => { try { return JSON.parse(localStorage.getItem(`psearch:${key}`) || "[]"); } catch { return []; } };
const writeHistory = (key, list) => { try { localStorage.setItem(`psearch:${key}`, JSON.stringify(list)); } catch { /* ignore */ } };

export const ProductSearchBar = ({ onSearch, maxLabel, testid = "product-search", historyKey, compact = false, allowDemo = false }) => {
  const { t, lang } = useI18n();
  const [q, setQ] = useState("");
  const [history, setHistory] = useState(() => (historyKey ? readHistory(historyKey) : []));
  if (insideDemo() && !allowDemo) {
    return (
      <div className={`rounded-2xl border border-xmas-gold/25 bg-xmas-gold/[0.06] ${compact ? "p-3" : "p-4"}`} data-testid={`${testid}-demo-disabled`}>
        <p className={`font-display text-xmas-gold ${compact ? "text-sm" : "text-base"}`}>{t("psearch.title")}</p>
        <div className="mt-3 flex gap-2 opacity-60">
          <input disabled value="" placeholder={t("psearch.placeholder")} data-testid={`${testid}-input`} className="flex-1 cursor-not-allowed rounded-xl border border-xmas-gold/20 bg-xmas-navy/40 px-4 py-2.5 text-sm text-xmas-snow outline-none" />
          <button type="button" disabled data-testid={`${testid}-submit`} className="btn-gold inline-flex cursor-not-allowed items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-bold uppercase tracking-wider"><Search className="h-4 w-4" /> {t("psearch.button")}</button>
        </div>
        <p className="mt-3 text-xs text-xmas-champagne" data-testid={`${testid}-demo-msg`}>{demoCopy(lang).landing.searchDisabled}</p>
      </div>
    );
  }

  const run = (v) => {
    if (historyKey) {
      const next = [v, ...history.filter((h) => h.toLowerCase() !== v.toLowerCase())].slice(0, MAX_HISTORY);
      setHistory(next); writeHistory(historyKey, next);
    }
    onSearch(v);
  };
  const submit = (e) => {
    e.preventDefault();
    const v = q.trim();
    if (v.length < 2) return;
    run(v);
  };
  const clearHistory = () => { setHistory([]); if (historyKey) writeHistory(historyKey, []); };

  return (
    <form onSubmit={submit} className={`rounded-2xl border border-xmas-gold/25 bg-xmas-gold/[0.06] ${compact ? "p-3" : "p-4"}`} data-testid={testid}>
      <p className={`font-display text-xmas-gold ${compact ? "text-sm" : "text-base"}`}>{t("psearch.title")}</p>
      <p className="mt-0.5 text-xs text-xmas-slate">{t("psearch.hint")}{maxLabel ? ` · ${t("psearch.max", { v: maxLabel })}` : ""}</p>
      <div className="mt-3 flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} maxLength={80} placeholder={t("psearch.placeholder")} data-testid={`${testid}-input`}
          className="min-w-0 flex-1 rounded-full border border-xmas-gold/20 bg-xmas-navy/60 px-4 py-2.5 text-sm text-xmas-snow outline-none placeholder:text-xmas-slate/70 focus:border-xmas-gold" />
        <button type="submit" disabled={q.trim().length < 2} data-testid={`${testid}-submit`}
          className="btn-gold inline-flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider disabled:opacity-40">
          <Search className="h-4 w-4" /> {t("psearch.button")}
        </button>
      </div>
      {history.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid={`${testid}-history`}>
          <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-xmas-slate"><History className="h-3 w-3" /> {t("psearch.recent")}</span>
          {history.map((h) => (
            <button key={h} type="button" onClick={() => { setQ(h); run(h); }} data-testid={`${testid}-history-item`}
              className="rounded-full border border-xmas-gold/25 bg-xmas-navy/50 px-3 py-1 text-xs text-xmas-snow transition hover:border-xmas-gold hover:text-xmas-gold">{h}</button>
          ))}
          <button type="button" onClick={clearHistory} aria-label={t("psearch.clear_history")} title={t("psearch.clear_history")} data-testid={`${testid}-history-clear`}
            className="rounded-full p-1 text-xmas-slate transition hover:bg-xmas-crimson/20 hover:text-xmas-crimson"><X className="h-3 w-3" /></button>
        </div>
      )}
    </form>
  );
};
